import {
    Box,
    Button,
    IconButton,
    Paper,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Chip,
    Tooltip,
    DialogContentText
} from "@mui/material";
import Cards from '../../ReusableComponents/Cards/Cards';
import React, { useEffect, useState } from "react";
import CustomSnackbar from '../../components/CustomSnackbar';
import api from '../../api/api';
import { ButtonStyles } from '../../Styles/ButtonStyles';
import AddCoupon from './AddCoupon';
import PersonOff from '@mui/icons-material/PersonOff';
import CloseIcon from "@mui/icons-material/Close";
import CancelIcon from "@mui/icons-material/Cancel";
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import en_ja from '../../translations/en_ja';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';

const Coupon = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarType, setSnackbarType] = useState("success");

    const [editMode, setEditMode] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [couponToDeactivate, setCouponToDeactivate] = useState(null);
    const [cardsData, setCardsData] = useState([]);

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);

    useEffect(() => {
        getAllCoupons();
        getAllCards()
    }, []);

    const getAllCards = () => {
        api.get("/v1/admin/coupon/stats")
            .then((res) => {
                setCardsData(res?.data || {})
            })
            .catch((err) => console.error(err));
    };

    const getAllCoupons = async () => {
        setLoading(true);
        setError(false);
        try {
            const res = await api.get("/v1/admin/get/all/coupons");
            const finalData = Array.isArray(res.data.message)
                ? res?.data?.message
                : (res?.data?.data || []);
            setCoupons(finalData);
        } catch (err) {
            console.error(err);
            setError(true);
            setSnackbarType("error");
            setSnackbarMessage(en_ja["Failed to fetch coupons"]);
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
        setSnackbarMessage("");
        setSnackbarType("success");
    };

    const handleAddCoupon = () => {
        setEditMode(false);
        setSelectedCoupon(null);
        setDialogOpen(true);
    };

    const handleDeactivate = (coupon) => {
        setCouponToDeactivate(coupon);
        setConfirmDialogOpen(true);
    };

    const handleCancelDeactivate = () => {
        setConfirmDialogOpen(false);
        setCouponToDeactivate(null);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        setSelectedCoupon(null);
        setEditMode(false);
    };

    const handleDialogSubmit = () => {
        getAllCoupons();
    };

    const handleConfirmDeactivate = async () => {
        if (!couponToDeactivate) return;

        const couponId = couponToDeactivate.id;
        const currentIsEnabled = couponToDeactivate.isEnabled;

        const obj = {
            couponId: couponId,
            isEnabled: !currentIsEnabled
        };

        setConfirmDialogOpen(false);
        setCouponToDeactivate(null);

        try {
            await api.put(`/v1/admin/update-coupon-status`, obj);
            const updatedStatus = obj.isEnabled ? en_ja["Active"] : en_ja["Inactive"];

            setSnackbarMessage(en_ja["Coupon status updated to"] + ` ${updatedStatus}`);
            setSnackbarType("success");
            setSnackbarOpen(true);
            getAllCoupons();
        } catch (err) {
            console.error(err);
            setSnackbarMessage(en_ja["Could not update coupon status"]);
            setSnackbarType("error");
            setSnackbarOpen(true);
        }
    };

    const handleDeleteClick = (coupon) => {
        setCouponToDelete(coupon);
        setDeleteDialogOpen(true);
    };

    const handleCancelDelete = () => {
        setDeleteDialogOpen(false);
        setCouponToDelete(null);
    };
    const handleConfirmDelete = async () => {
        if (couponToDelete) {
            try {
                await api.delete(`/v1/admin/delete-coupon/${couponToDelete.id}`);
                setDeleteDialogOpen(false);
                setCouponToDelete(null);
                setSnackbarMessage(en_ja["Coupon deleted successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
                getAllCoupons();
                getAllCards();
            } catch (error) {
                console.error("Error deleting Coupon:", error);
            }
        }
    };

    const formatPrice = (amount) => {
        if (amount === 0) return "¥0";
        return `¥${amount.toLocaleString()}`;
    };

    return (
        <Box sx={{ mt: 2 }}>
            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={handleSnackbarClose}
            />

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                    gap: 3,
                    width: "100%",
                }}
            >
                <Cards
                    title={en_ja["Total Coupons"]}
                    value={cardsData?.totalCoupons ?? 0}
                    icon={<ConfirmationNumberIcon sx={{ color: "white", fontSize: 24 }} />}
                />
                <Cards
                    title={en_ja["Active Coupons"]}
                    value={cardsData?.activeCoupons ?? 0}
                    icon={<LocalOfferIcon sx={{ color: "white", fontSize: 24 }} />}
                />
                <Cards
                    title={en_ja["Inactive Coupons"]}
                    value={cardsData?.inactiveCoupons ?? 0}
                    icon={<CancelIcon sx={{ color: "white", fontSize: 24 }} />}
                />
                <Cards
                    title={en_ja["Total Usage"]}
                    value={cardsData?.totalUsage ?? 0}
                    icon={<ReceiptLongIcon sx={{ color: "white", fontSize: 24 }} />}
                />
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", my: 3 }}>
                <Button sx={ButtonStyles} onClick={handleAddCoupon}>
                    {en_ja["Add Coupon"]}
                </Button>
            </Box>

            <Typography
                variant="h5"
                fontWeight="700"
                color="#1a1a1a"
                sx={{ fontSize: "20px", mb: 3 }}
            >
                {en_ja["Coupons"]}
            </Typography>

            {loading ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
                        {en_ja["Loading coupons..."]}
                    </Typography>
                </Box>
            ) : error ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="error">
                        {en_ja["Failed to fetch coupons"]}
                    </Typography>
                </Box>
            ) : coupons.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" color="error">
                        {en_ja["No coupons available"]}
                    </Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                        gap: 3,
                        mt: 3,
                    }}
                >
                    {coupons?.map((coupon) => (
                        <Paper
                            key={coupon.id}
                            sx={{
                                p: 3,
                                borderRadius: 2,
                                border: "1px solid #e9ecef",
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                                position: "relative",
                                "&:hover": {
                                    transform: "translateY(-4px)",
                                    boxShadow: "0 12px 24px rgba(0,0,0,0.1)"
                                },
                            }}
                        >
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>

                                <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                                    <Typography variant="subtitle1" fontWeight={700}>
                                        {coupon?.coupon_name}
                                    </Typography>

                                    {(() => {
                                        const toDate = new Date(coupon.to_date);
                                        const today = new Date();
                                        toDate.setHours(0, 0, 0, 0);
                                        today.setHours(0, 0, 0, 0);
                                        return toDate < today;
                                    })() ? (
                                        <Chip
                                            label={en_ja["EXPIRED"]}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#9ca3af',
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '10px'
                                            }}
                                        />
                                    ) : (
                                        <Chip
                                            label={coupon?.isEnabled ? en_ja["ACTIVE"] : en_ja["INACTIVE"]}
                                            size="small"
                                            sx={{
                                                backgroundColor: coupon?.isEnabled ? '#22c55e' : '#ef4444',
                                                color: 'white',
                                                fontWeight: 600,
                                                fontSize: '10px'
                                            }}
                                        />
                                    )}
                                </Box>

                                <Box sx={{ display: "flex", gap: 1 }}>

                                    <Tooltip
                                        title={
                                            coupon?.isEnabled
                                                ? en_ja["Deactivate Coupon"] || "Deactivate Coupon"
                                                : en_ja["Activate Coupon"] || "Activate Coupon"
                                        }
                                        arrow
                                    >
                                        <IconButton size="small" onClick={() => handleDeactivate(coupon)}>
                                            {coupon?.isEnabled ? (
                                                <PersonIcon sx={{ color: "#dc3545" }} />
                                            ) : (
                                                <PersonOff sx={{ color: "rgb(0 0 0 / 54%)" }} />
                                            )}
                                        </IconButton>
                                    </Tooltip>

                                    <Tooltip title={en_ja["Delete Coupon"]} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteClick(coupon)}
                                            sx={{
                                                color: "error.main",
                                                "&:hover": {
                                                    backgroundColor: "error.light"
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>

                                </Box>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>

                                <Chip
                                    label={coupon.coupon_code}
                                    size="small"
                                    sx={{
                                        backgroundColor: "#2491ef",
                                        color: 'white',
                                        fontWeight: 600,
                                        fontSize: '10px',
                                    }}
                                />

                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {en_ja["Type"]}:{" "}
                                    <Box component="span" color="text.secondary">
                                        {coupon.type}
                                    </Box>
                                </Typography>

                            </Box>

                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>{coupon.description}</Typography>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {en_ja["Discount"]}:{" "}
                                    <Box component="span" fontSize="14px" fontWeight="600" color="#28a745">
                                        {formatPrice(coupon.amount)}
                                    </Box>
                                </Typography>

                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {en_ja["Minimum Order"]}:{" "}
                                    <Box component="span" fontSize="14px" fontWeight="600" color="#28a745">
                                        {formatPrice(coupon.minimum_transaction_amount)}
                                    </Box>
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                    {en_ja["Created"]}:{" "}
                                    <Box component="span" color="text.secondary">
                                        {new Date(coupon.created_at).toLocaleDateString()}
                                    </Box>
                                </Typography>

                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    {new Date(coupon.from_date).toLocaleDateString()} -{" "}
                                    {new Date(coupon.to_date).toLocaleDateString()}
                                </Typography>
                            </Box>
                        </Paper>
                    ))}
                </Box>
            )}

            {/* Confirmation Dialog */}
            <Dialog
                key={couponToDeactivate?.id || 'closed'}
                open={confirmDialogOpen}
                onClose={handleCancelDeactivate}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                        {couponToDeactivate?.isEnabled ? en_ja["Deactivate Coupon"] : en_ja["Activate Coupon"]}
                    </Box>
                    <IconButton onClick={handleCancelDeactivate}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ textAlign: "center", pt: 1, pb: 3 }}>
                    {couponToDeactivate && (
                        <>
                            <Typography variant="body1" sx={{ mb: 1 }}>
                                {en_ja["Are you sure you want to"]} {couponToDeactivate?.isEnabled ? en_ja["deactivate"] : en_ja["activate"]} "
                                {couponToDeactivate?.coupon_name}"?
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {en_ja["This action can be reversed later by"]} {couponToDeactivate?.isEnabled ? en_ja["reactivating"] : en_ja["deactivating"]} {en_ja["the coupon"]}.
                            </Typography>
                        </>
                    )}
                </DialogContent>

                <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancelDeactivate}
                        sx={{ textTransform: "none" }}
                    >
                        {en_ja["No, Keep"]} {couponToDeactivate?.isEnabled ? en_ja["Active"] : en_ja["Inactive"]}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeactivate}
                        color={couponToDeactivate?.isEnabled ? "error" : "success"}
                        sx={{ textTransform: "none" }}
                    >
                        {en_ja["Yes,"]} {couponToDeactivate?.isEnabled ? en_ja["Deactivate"] : en_ja["Activate"]}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={deleteDialogOpen && !!couponToDelete}
                onClose={handleCancelDelete}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="delete-dialog-title" sx={{ color: "error.main" }}>
                    {en_ja["Confirm Delete"]}
                </DialogTitle>

                <DialogContent>
                    <DialogContentText>
                        {en_ja["Are you sure you want to delete the coupon"]}{" "}
                        <strong>{couponToDelete?.coupon_name || ""}</strong>?{" "}
                        {en_ja["This action cannot be undone."]}
                    </DialogContentText>
                </DialogContent>

                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={handleCancelDelete} color="primary">
                        {en_ja["Cancel"]}
                    </Button>

                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        autoFocus
                    >
                        {en_ja["Delete"]}
                    </Button>
                </DialogActions>
            </Dialog>

            <AddCoupon
                dialogOpen={dialogOpen}
                onClose={handleDialogClose}
                editMode={editMode}
                couponData={selectedCoupon}
                onSubmit={handleDialogSubmit}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
            />
        </Box>
    );
};

export default Coupon;
